import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { FiArrowLeft, FiClock, FiCheckCircle } from 'react-icons/fi';
import { Button } from '@/components/common/Button';
import api from '@/utils/api';
import { useAuth } from '@/context/AuthContext';
import toast from 'react-hot-toast';
import { format, isPast } from 'date-fns';

interface Action {
  _id: string;
  title: string;
  description: string;
  priority: 'low' | 'medium' | 'high' | 'urgent';
  status: 'pending' | 'in_progress' | 'completed' | 'on_hold' | 'cancelled';
  category: string;
  dueDate?: string;
  progress: number;
  team?: {
    _id: string;
    name: string;
  };
  createdAt: string;
}

const Container = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  padding: ${({ theme }) => theme.spacing.xl};
  overflow: hidden;

  @media (max-width: 1024px) {
    padding: ${({ theme }) => `${theme.spacing.lg} ${theme.spacing.md}`};
  }

  @media (max-width: 768px) {
    padding: ${({ theme }) => `${theme.spacing.md} ${theme.spacing.sm}`};
    overflow: visible;
  }

  @media (max-width: 480px) {
    padding: ${({ theme }) => theme.spacing.sm};
  }
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.md};
  margin-bottom: ${({ theme }) => theme.spacing.lg};
  flex-shrink: 0;

  @media (max-width: 768px) {
    flex-wrap: wrap;
    gap: ${({ theme }) => theme.spacing.sm};
    margin-bottom: ${({ theme }) => theme.spacing.md};
  }
`;

const BackButton = styled(Button)`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm};
  padding: ${({ theme }) => `${theme.spacing.sm} ${theme.spacing.md}`};

  @media (max-width: 480px) {
    padding: ${({ theme }) => `${theme.spacing.xs} ${theme.spacing.sm}`};
    font-size: ${({ theme }) => theme.fontSizes.sm};

    svg {
      width: 16px;
      height: 16px;
    }
  }
`;

const Title = styled.h1`
  font-size: ${({ theme }) => theme.fontSizes['3xl']};
  color: ${({ theme }) => theme.colors.onBackground};
  font-weight: ${({ theme }) => theme.fontWeights.bold};
  flex: 1;

  @media (max-width: 480px) {
    font-size: ${({ theme }) => theme.fontSizes['2xl']};
  }
`;

const ActionCount = styled.span`
  background: ${({ theme }) => theme.colors.primary}15;
  color: ${({ theme }) => theme.colors.primary};
  padding: ${({ theme }) => theme.spacing.xs} ${({ theme }) => theme.spacing.md};
  border-radius: ${({ theme }) => theme.borderRadius.full};
  font-size: ${({ theme }) => theme.fontSizes.sm};
  font-weight: ${({ theme }) => theme.fontWeights.semibold};
`;

const Content = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: ${({ theme }) => theme.spacing.lg};
  max-width: 1200px;
  width: 100%;
  margin: 0 auto;

  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-track {
    background: ${({ theme }) => theme.colors.surface};
    border-radius: ${({ theme }) => theme.borderRadius.md};
  }

  &::-webkit-scrollbar-thumb {
    background: ${({ theme }) => theme.colors.border};
    border-radius: ${({ theme }) => theme.borderRadius.md};

    &:hover {
      background: ${({ theme }) => theme.colors.muted};
    }
  }

  @media (max-width: 1024px) {
    padding: ${({ theme }) => theme.spacing.md};
  }

  @media (max-width: 768px) {
    padding: ${({ theme }) => theme.spacing.md} 0;
    overflow: visible;
  }

  @media (max-width: 640px) {
    padding: 0;
  }
`;

const ActionsGrid = styled.div`
  width: 100%;
  display: grid;
  grid-template-columns: 1fr;
  gap: clamp(1rem, 2vw, 1.5rem);
`;

const ActionCard = styled(motion.div)<{ $priority: string; $isOverdue: boolean; $completed: boolean }>`
  background: ${({ theme }) => 
    theme.colors.background === '#0f172a' 
      ? 'linear-gradient(135deg, rgba(30, 41, 59, 0.9) 0%, rgba(15, 23, 42, 0.9) 100%)'
      : 'rgba(255, 255, 255, 0.7)'};
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  border-radius: ${({ theme }) => theme.borderRadius.xl};
  padding: ${({ theme }) => theme.spacing.lg};
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.08);
  border-left: 4px solid ${({ $completed }) => ($completed ? '#10b981' : '#ef4444')};
  border-top: 1px solid ${({ theme }) => 
    theme.colors.background === '#0f172a' 
      ? 'rgba(255, 255, 255, 0.1)'
      : 'rgba(255, 255, 255, 0.18)'};
  border-right: 1px solid ${({ theme }) => 
    theme.colors.background === '#0f172a' 
      ? 'rgba(255, 255, 255, 0.1)'
      : 'rgba(255, 255, 255, 0.18)'};
  border-bottom: 1px solid ${({ theme }) => 
    theme.colors.background === '#0f172a' 
      ? 'rgba(255, 255, 255, 0.1)'
      : 'rgba(255, 255, 255, 0.18)'};
  transition: ${({ theme }) => theme.transitions.default};
  cursor: pointer;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 12px 40px rgba(0, 0, 0, 0.12);
  }

  &:active {
    transform: translateY(-2px);
  }

  @media (max-width: 640px) {
    padding: ${({ theme }) => theme.spacing.md};
  }
`;

const ActionHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: ${({ theme }) => theme.spacing.md};
  gap: ${({ theme }) => theme.spacing.sm};

  @media (max-width: 640px) {
    flex-direction: column;
    align-items: flex-start;
  }
`;

const ActionTitle = styled.h3`
  font-size: ${({ theme }) => theme.fontSizes.lg};
  color: ${({ theme }) => theme.colors.onBackground};
  font-weight: ${({ theme }) => theme.fontWeights.semibold};
  margin: 0;
  line-height: 1.4;
  flex: 1;
`;

const CompletedIcon = styled.div`
  width: 28px;
  height: 28px;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  border: 2px solid ${({ theme }) => theme.colors.success};
  background: ${({ theme }) => theme.colors.success};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: ${({ theme }) => theme.transitions.default};

  svg {
    width: 18px;
    height: 18px;
    color: white;
  }
`;

const ActionDescription = styled.p`
  font-size: ${({ theme }) => theme.fontSizes.sm};
  color: ${({ theme }) => theme.colors.muted};
  line-height: 1.6;
  margin: 0 0 ${({ theme }) => theme.spacing.md} 0;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const ActionMeta = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.sm};
  margin-bottom: ${({ theme }) => theme.spacing.md};
`;

const MetaItem = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xs};
  font-size: ${({ theme }) => theme.fontSizes.sm};
  color: ${({ theme }) => theme.colors.muted};

  svg {
    width: 14px;
    height: 14px;
  }
`;

const StatusContainer = styled.div`
  min-width: 160px;
  max-width: 200px;
  align-self: flex-start;
`;

const StatusSelect = styled.select`
  width: 100%;
  padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.md};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  border: 1px solid ${({ theme }) => theme.colors.border};
  background: ${({ theme }) => theme.colors.surface};
  color: ${({ theme }) => theme.colors.onBackground};
  font-size: ${({ theme }) => theme.fontSizes.sm};
  font-weight: ${({ theme }) => theme.fontWeights.medium};
  cursor: pointer;
  transition: ${({ theme }) => theme.transitions.default};

  &:hover {
    border-color: ${({ theme }) => theme.colors.primary};
  }

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
    box-shadow: 0 0 0 3px ${({ theme }) => theme.colors.primary}20;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: ${({ theme }) => theme.spacing['3xl']};
  color: ${({ theme }) => theme.colors.muted};
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: ${({ theme}) => theme.spacing.lg};
`;

const EmptyIcon = styled.div`
  font-size: 4rem;
  opacity: 0.5;
`;

const EmptyTitle = styled.h2`
  font-size: ${({ theme }) => theme.fontSizes['2xl']};
  color: ${({ theme }) => theme.colors.onBackground};
  font-weight: ${({ theme }) => theme.fontWeights.semibold};
  margin: 0;
`;

const EmptyText = styled.p`
  font-size: ${({ theme }) => theme.fontSizes.base};
  color: ${({ theme }) => theme.colors.muted};
  margin: 0;
  max-width: 400px;
`;

export const ActionsPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [actions, setActions] = useState<Action[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user?.role === 'program_manager') {
      fetchActions();
    } else {
      setLoading(false);
    }
  }, [user]);

  const fetchActions = async () => {
    try {
      setLoading(true);
      const response = await api.get('/actions');
      setActions(response.data.data || []);
    } catch (error: any) {
      console.error('Error fetching actions:', error);
      toast.error(error.response?.data?.message || 'Failed to load actions');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusUpdate = async (actionId: string, newStatus: string) => {
    // Optimistically update local status and progress
    setActions((prev) =>
      prev.map((action) =>
        action._id === actionId
          ? {
              ...action,
              status: newStatus as Action['status'],
              progress: newStatus === 'completed' ? 100 : action.progress,
            }
          : action
      )
    );

    try {
      const payload: { status: string; note: string; progress?: number } = {
        status: newStatus,
        note: `Status updated to ${newStatus}`,
      };

      if (newStatus === 'completed') {
        payload.progress = 100;
      }

      await api.patch(`/actions/${actionId}/status`, payload);
      
      toast.success('Action status updated successfully');
      
      // Refresh actions
      fetchActions();
    } catch (error: any) {
      console.error('Error updating action:', error);
      toast.error(error.response?.data?.message || 'Failed to update action status');
    }
  };

  if (user?.role !== 'program_manager') {
    return (
      <Container>
        <Header>
          <BackButton variant="outline" onClick={() => navigate('/')}>
            <FiArrowLeft /> Back
          </BackButton>
          <Title>My Actions</Title>
        </Header>
        <EmptyState>
          <EmptyIcon>🔒</EmptyIcon>
          <EmptyTitle>Access Restricted</EmptyTitle>
          <EmptyText>
            This feature is only available for Program Managers.
          </EmptyText>
        </EmptyState>
      </Container>
    );
  }

  if (loading) {
    return (
      <Container>
        <Header>
          <BackButton variant="outline" onClick={() => navigate('/')}>
            <FiArrowLeft /> Back
          </BackButton>
          <Title>My Actions</Title>
        </Header>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
        </div>
      </Container>
    );
  }

  if (actions.length === 0) {
    return (
      <Container>
        <Header>
          <BackButton variant="outline" onClick={() => navigate('/')}>
            <FiArrowLeft /> Back
          </BackButton>
          <Title>My Actions</Title>
        </Header>
        <EmptyState>
          <EmptyIcon>✅</EmptyIcon>
          <EmptyTitle>No actions assigned</EmptyTitle>
          <EmptyText>You don't have any actions assigned at the moment.</EmptyText>
        </EmptyState>
      </Container>
    );
  }

  return (
    <Container>
      <Header>
        <BackButton variant="outline" onClick={() => navigate('/')}>
          <FiArrowLeft /> Back
        </BackButton>
        <Title>Program Director Dashboard</Title>
        <ActionCount>{actions.length} {actions.length === 1 ? 'Action' : 'Actions'}</ActionCount>
      </Header>

      <Content>
        <ActionsGrid>
          {actions.map((action, index) => {
            const isOverdue = action.dueDate && isPast(new Date(action.dueDate)) && action.status !== 'completed';
            const tabletStatus = action.status === 'completed' ? 'completed' : 'pending';

            return (
              <ActionCard
                key={action._id}
                $priority={action.priority}
                $isOverdue={!!isOverdue}
                $completed={action.status === 'completed'}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                whileTap={{ scale: 0.98 }}
              >
                <ActionHeader>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1 }}>
                    {action.status === 'completed' && (
                      <CompletedIcon>
                        <FiCheckCircle />
                      </CompletedIcon>
                    )}
                    <ActionTitle>{action.title}</ActionTitle>
                  </div>
                  <StatusContainer>
                    <StatusSelect
                      value={tabletStatus}
                      onChange={(e) => {
                        e.stopPropagation();
                        handleStatusUpdate(action._id, e.target.value);
                      }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <option value="completed">Completed</option>
                      <option value="pending">Not - Completed</option>
                    </StatusSelect>
                  </StatusContainer>
                </ActionHeader>

                <ActionDescription>{action.description}</ActionDescription>

                <ActionMeta>
                  {action.dueDate && (
                    <MetaItem>
                      <FiClock />
                      <span style={{ color: isOverdue ? '#ef4444' : undefined }}>
                        Due: {format(new Date(action.dueDate), 'MMM dd, yyyy')}
                        {isOverdue && ' (Overdue)'}
                      </span>
                    </MetaItem>
                  )}
                </ActionMeta>
              </ActionCard>
            );
          })}
        </ActionsGrid>
      </Content>
    </Container>
  );
}
;
